<template>
  <div class="kb-member-summary">
    <div
      v-if="member.banned"
      class="kb-member-banned"
      role="status"
    >
      <i
        class="pi pi-ban"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <div class="font-semibold">
          Banned{{ bannedOn ? ` since ${bannedOn}` : '' }}
        </div>
        <div
          v-if="showBanReason && banReason"
          class="kb-member-banned-reason"
        >
          {{ banReason }}
        </div>
      </div>
    </div>

    <ul
      v-if="joinedOn || isVip || roles.length"
      class="kb-member-facts"
    >
      <li v-if="joinedOn">
        Joined {{ joinedOn }}
      </li>
      <li v-if="isVip">
        <span class="kb-tag kb-tag-honor">VIP {{ paradeYear }}</span>
      </li>
      <li v-if="roles.length">
        Admin: {{ roles.join(', ') }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import type { Member } from '~/shared/types/member'
import { formatDate } from '~/utils/dateUtils'
import { adminRoleLabels, hasVipTicket } from '~/utils/memberFacts'

// At-a-glance facts that used to be columns in the admin Members table.
// The ban reason can be personal, so only full admins see it, as in the
// Admin section further down the drawer.
const props = defineProps<{
  member: Member
  showBanReason?: boolean
}>()

const { paradeYear } = useParadeYear()

// Firestore hands back Timestamps where the type says Date, and a pending
// serverTimestamp() until the write lands; only format what is a real date.
type DateLike = Date | { toDate: () => Date }
const isDateLike = (value: unknown): value is DateLike =>
  value instanceof Date || typeof (value as { toDate?: unknown } | null)?.toDate === 'function'

const joinedOn = computed(() => isDateLike(props.member.created_at) ? formatDate(props.member.created_at) : '')
const bannedOn = computed(() => isDateLike(props.member.banned_at) ? formatDate(props.member.banned_at) : '')
const banReason = computed(() => typeof props.member.banned_reason === 'string' ? props.member.banned_reason.trim() : '')
const isVip = computed(() => hasVipTicket(props.member, paradeYear.value))
const roles = computed(() => adminRoleLabels(props.member))
</script>

<style scoped>
.kb-member-summary {
  display: grid;
  gap: 0.625rem;
}

.kb-member-summary:empty {
  display: none;
}

.kb-member-banned {
  display: flex;
  gap: 0.625rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--kb-alert);
  border-left-width: 3px;
  border-radius: 4px;
  background: var(--kb-alert-wash);
  color: var(--kb-alert);
  font-size: 0.875rem;
}

.kb-member-banned .pi {
  margin-top: 0.15rem;
}

.kb-member-banned-reason {
  margin-top: 0.125rem;
  color: var(--kb-fg);
  overflow-wrap: anywhere;
}

/* Inset to line up with the section titles below (1rem padding + 1px border);
   the banned bar above stays full width like the sections */
.kb-member-facts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0;
  margin: 0;
  padding: 0 calc(1rem + 1px);
  list-style: none;
  font: 12px/1.5 var(--kb-font-mono);
  color: var(--kb-muted);
}

/* The dot ends each fact, so a wrapped line never starts with one */
.kb-member-facts li:not(:last-child)::after {
  content: '·';
  margin: 0 0.5rem;
  color: var(--kb-dim);
}
</style>
